<script setup lang="ts">
import { AnswerType, AssessmentChoice, AssessmentQuestion } from '../../../../../types/assessment';
import AudioPlayer from '../../../../General/AudioPlayer.vue';
import ImageContainer from '../../../../General/ImageContainer.vue';
import { computed, ref } from 'vue';
import { useClassStore, useUserStore } from '../../../../../stores';
import { UserRole } from '../../../../../types/user';
import IconCheckbox from '../../../../SVG/IconCheckbox.vue';
import { QuizAudioKey } from '../../../../../types/quiz';

const { question, showSubmission, answers, showAnswers, disabled } = defineProps<{
    question: AssessmentQuestion,
    answers: string[],
    showAnswers?: boolean,
    showSubmission?: boolean
    disabled?: boolean
}>()

const emit = defineEmits(['change'])

// const quizStore = useQuizStore();
const userStore = useUserStore();
const classStore = useClassStore();
const studentAnswers = ref<string[]>(answers);
// const fontSizeClasses = ref<Record<string, string>>({}); // Store font size classes for each choice
// Calculate the number of columns based on the number of choices
const colsCalculated = computed(() => {
    const answerColsMap = [
        'md:w-full',
        'md:w-1/2',
        'md:w-1/3',
        'md:w-1/4',
        'md:w-1/5',
        'md:w-1/6'
    ]
    return answerColsMap[question.choices.length - 1] || 'md:w-1/6';
});

function selectedCount(choice: AssessmentChoice) {
    return classStore.selectedClassDetails?.students?.reduce((count, student) => {
        return count + (student.quizQuestion?.some((question) => question.answer.find((a) => a.answer === choice.id.toString())) ? 1 : 0);
    }, 0);
};

function answerClass(choice: AssessmentChoice) {
    if (!showAnswers) {
        // If the maximum number of answers is reached and the current choice is not selected
        return userStore.userRole === UserRole.student && answers.length >= question.totalCorrectAnswer && !answers.includes(choice.id.toString());
    }
    // Show correct/incorrect answers when `showAnswer` is true
    if (userStore.userRole === UserRole.student) {
        return !answers.includes(choice.id.toString())
    }
    // For teachers, mark incorrect answers
    return choice.isCorrect === 0;
}

function handleAnswerChange(choiceId: string, event: MouseEvent) {
    // Don't process clicks that originated from AudioPlayer
    if (event.target && (event.target as HTMLElement).closest('.audio-player')) {
        event.preventDefault();
        return;
    }
    if (userStore.userRole === UserRole.teacher || disabled) {
        // If the user is a teacher or disabled, do not allow answer changes
        return;
    }
    if (question.totalCorrectAnswer === 1) {
        // If only one answer is allowed, replace the current answer
        studentAnswers.value = [choiceId];
    } else if (!studentAnswers.value.includes(choiceId)) {
        // If the answer is not already selected, add it to the list of answers
        // Check if the maximum number of answers is reached
        if (studentAnswers.value.length < question.totalCorrectAnswer) {
            // Add the new answer
            studentAnswers.value.push(choiceId);
        }
    } else {
        // If the answer is already selected, remove it from the list of answers
        studentAnswers.value = studentAnswers.value.filter(answer => answer !== choiceId);
    }

    emit('change', studentAnswers.value);

}

function checkboxState(choice: AssessmentChoice) {
    // If the user is a teacher, return the checkbox state based on the correct answer
    if (userStore.userRole === UserRole.teacher && showAnswers) {
        return choice.isCorrect === 1 ? 'checked' : 'unchecked';
    }

    // If the user is a student, return the checkbox state based on their answers
    if (userStore.userRole === UserRole.student) {
        if (showAnswers) {
            // If answers are shown, return the state based on the correct answer
            if (answers.length === 0) {
                return 'unchecked';
            }
            if (answers.includes(choice.id.toString()) && choice.isCorrect === 1) {
                return 'checked';
            } else if (answers.includes(choice.id.toString()) && choice.isCorrect === 0) {
                return 'incorrect';
            }
        }
        return studentAnswers.value.includes(choice.id.toString()) ? 'checked' : 'unchecked';
    }
    // Default state for other user roles (if any)
    return 'unchecked';
}


// function calculateFontSize(choice: AssessmentChoice): string {
//     const container = document.querySelector(`[data-choice-id="${choice.id}"]`);
//     if (!container) return 'text-lg';

//     const content = container.querySelector('.choice-content');
//     if (!content) return 'text-lg';

//     const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
//     const { scrollWidth: contentWidth, scrollHeight: contentHeight } = content;

//     if (contentWidth > containerWidth * 1.5 || contentHeight > containerHeight * 1.5) {
//         return 'text-sm';
//     }
//     if (contentWidth > containerWidth * 1.2 || contentHeight > containerHeight * 1.2) {
//         return 'text-md';
//     }
//     if (contentWidth > containerWidth || contentHeight > containerHeight) {
//         return 'text-lg';
//     }
//     return 'text-lg';
// }

// onMounted(() => {
//     question.choices.forEach((choice) => {
//         fontSizeClasses.value[choice.id] = calculateFontSize(choice);
//     });
// });


// watch(() => question.choices, (newChoices) => {
//     newChoices.forEach((choice) => {
//         fontSizeClasses.value[choice.id] = calculateFontSize(choice);
//     });
// });
</script>

<template>
    <div class="w-full h-full overflow-auto flex  bg-black/20 rounded-lg backdrop-blur-sm">
        <div class="w-1/2 p-1 md:p-3 grow shrink-0 h-full overflow-hidden flex" :class="colsCalculated" v-for="choice in question.choices" :key="choice.id">
            <div class="flex-1 flex flex-col items-center rounded-lg gap-1 md:gap-3 border border-b-4 p-1 md:p-3 cursor-pointer text-black bg-white border-neutral-400 dark:border-sky-100" :class="[
                {
                    '!cursor-not-allowed': userStore.userRole === UserRole.teacher || disabled,
                    '!border-rose-500 !bg-red-100': showAnswers && (userStore.userRole === UserRole.student && answers.includes(choice.id.toString()) && choice.isCorrect == 0),
                    '!border-lime-500 !bg-lime-100 dark:!bg-lime-300': showAnswers && (choice.isCorrect === 1 || (answers.includes(choice.id.toString()) && choice.isCorrect === 1)),
                    'opacity-50': answerClass(choice)
                }]" @click="handleAnswerChange(choice.id.toString(), $event)">

                <!-- choice top -->
                <div class="flex gap-3 justify-between w-full text-stone-700" v-if="(userStore.userRole === UserRole.teacher && showSubmission) || question.totalCorrectAnswer > 1">
                    <!-- choice letter -->
                    <IconCheckbox v-if="question.totalCorrectAnswer > 1" :state="checkboxState(choice)" class="shrink-0 border-[2px] border-stone-700 size-5 lg:size-6 rounded-md" />

                    <!-- count submited students -->
                    <div v-if="userStore.userRole === UserRole.teacher && showSubmission" class="flex justify-center gap-1 items-start text-xl font-bold" :class="{ 'grow': question.totalCorrectAnswer <= 1 }">
                        <p>{{ selectedCount(choice) }}</p>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 20V17.2C1 16.6334 1.146 16.1127 1.438 15.638C1.73 15.1634 2.11733 14.8007 2.6 14.55C3.63333 14.0334 4.68333 13.646 5.75 13.388C6.81667 13.13 7.9 13.0007 9 13C10.1 12.9994 11.1833 13.1287 12.25 13.388C13.3167 13.6474 14.3667 14.0347 15.4 14.55C15.8833 14.8 16.271 15.1627 16.563 15.638C16.855 16.1134 17.0007 16.634 17 17.2V20H1ZM19 20V17C19 16.2667 18.796 15.5624 18.388 14.887C17.98 14.2117 17.4007 13.6327 16.65 13.15C17.5 13.25 18.3 13.421 19.05 13.663C19.8 13.905 20.5 14.2007 21.15 14.55C21.75 14.8834 22.2083 15.254 22.525 15.662C22.8417 16.07 23 16.516 23 17V20H19ZM9 12C7.9 12 6.95833 11.6084 6.175 10.825C5.39167 10.0417 5 9.10003 5 8.00003C5 6.90003 5.39167 5.95836 6.175 5.17503C6.95833 4.39169 7.9 4.00003 9 4.00003C10.1 4.00003 11.0417 4.39169 11.825 5.17503C12.6083 5.95836 13 6.90003 13 8.00003C13 9.10003 12.6083 10.0417 11.825 10.825C11.0417 11.6084 10.1 12 9 12ZM19 8.00003C19 9.10003 18.6083 10.0417 17.825 10.825C17.0417 11.6084 16.1 12 15 12C14.8167 12 14.5833 11.9794 14.3 11.938C14.0167 11.8967 13.7833 11.8507 13.6 11.8C14.05 11.2667 14.396 10.675 14.638 10.025C14.88 9.37503 15.0007 8.70003 15 8.00003C14.9993 7.30003 14.8787 6.62503 14.638 5.97503C14.3973 5.32503 14.0513 4.73336 13.6 4.20003C13.8333 4.11669 14.0667 4.06236 14.3 4.03703C14.5333 4.01169 14.7667 3.99936 15 4.00003C16.1 4.00003 17.0417 4.39169 17.825 5.17503C18.6083 5.95836 19 6.90003 19 8.00003Z"
                                fill="currentColor" />
                        </svg>
                    </div>
                </div>
                <!-- choice content -->
                <div :data-choice-id="choice.id" class="flex w-full flex-1 max-h-full overflow-auto justify-center items-center">
                    <div v-if="question.answerType === AnswerType.TEXT" class="choice-content flex-1 flex justify-center items-center font-bold text-center break-words whitespace-pre-line" style="font-size: clamp(1em, 1.5em, 2em);">
                        {{ choice.content }}
                    </div>
                    <ImageContainer class="h-full" imgClass="object-contain h-full flex-1" :src="choice.answerUrl" :alt="choice.content" v-else-if="question.answerType === AnswerType.IMAGE" />
                    <AudioPlayer v-else-if="question.answerType === AnswerType.AUDIO" :src="choice.answerUrl" :audio-key="QuizAudioKey.answer" class="w-full aspect-[4/3] mx-auto max-w-14 flex-1" />
                </div>
            </div>
        </div>
    </div>
</template>